import React, { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    IconButton,
    Chip,
    Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { beautifyNumber } from '../../Utils/FormatNumber';
import DocumentService from '../../Services/documents.service';
import EnumsService from '../../Services/enums.service';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';

const headerCellSx = {
    fontWeight: 700,
    fontSize: 12,
    backgroundColor: '#304FFF',
    color: '#ffffff',
    textAlign: 'left',
};

const subHeaderCellSx = {
    fontWeight: 700,
    fontSize: 11,
    backgroundColor: '#BFC8FF',
    color: '#304FFF',
    textAlign: 'left',
    padding: '2px 6px',
};

const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    const day = ('0' + d.getDate()).slice(-2);
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

const RelatedPaymentAcceptancesModal = ({
    open,
    onClose,
    direction,
    currentEditedItem, // The Income/Expense currently being edited
    client,
    token,
}) => {
    const { t } = useTranslation();
    const { handleDocumentRowClick } = useHamburgerMenu();
    const [relatedPayments, setRelatedPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openRows, setOpenRows] = useState({});
    const [accountCardMap, setAccountCardMap] = useState({});

    const sourceDocType = currentEditedItem?.docType;

    const openPdf = (url) => {
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handlePaymentRowClick = (payment) => {
        if (!payment?.id || !handleDocumentRowClick) return;
        const enrichedList = relatedPayments.map((p) => ({
            ...p,
            docType: 'Checks',
            status: 'notDone',
        }));
        handleDocumentRowClick(payment.id, 'PaymentAcceptances', enrichedList);
        if (typeof onClose === 'function') onClose();
    };

    const accountName = (id) => (id != null && accountCardMap[id]) ? accountCardMap[id] : (id ?? '-');

    const fetchAccountCards = useCallback(async () => {
        if (!client?.caseId || !token) return;
        try {
            const response = await EnumsService.GetAccountCardsByCaseId(client.caseId, token);
            if (response.ok) {
                const data = await response.json();
                const map = {};
                (Array.isArray(data) ? data : []).forEach((card) => {
                    if (card.cardId != null) map[card.cardId] = card.name;
                });
                setAccountCardMap(map);
            }
        } catch (error) {
            console.error('Error fetching account cards:', error);
        }
    }, [client?.caseId, token]);

    const fetchRelatedPayments = useCallback(async () => {
        if (!currentEditedItem?.id || !token || !client?.caseId) {
            return;
        }
        if (sourceDocType !== 'Incomes' && sourceDocType !== 'Expenses') {
            setRelatedPayments([]);
            return;
        }

        setLoading(true);
        try {
            const ids = sourceDocType === 'Incomes'
                ? { incomeId: currentEditedItem.id }
                : { expenseId: currentEditedItem.id };

            const response = await DocumentService.GetPaymentAcceptancesByRelatedSource(
                client.caseId,
                ids,
                token
            );

            if (response.ok) {
                const data = await response.json();
                setRelatedPayments(Array.isArray(data) ? data : []);
            } else {
                setRelatedPayments([]);
            }
        } catch (error) {
            console.error('Error fetching related payment acceptances:', error);
            toast.error(t('ErrorFetchingRelatedPayments') || 'Error fetching related payments');
            setRelatedPayments([]);
        } finally {
            setLoading(false);
        }
    }, [currentEditedItem?.id, sourceDocType, client?.caseId, token, t]);

    useEffect(() => {
        if (open && currentEditedItem?.id) {
            fetchAccountCards();
            fetchRelatedPayments();
        } else if (!open) {
            setRelatedPayments([]);
            setOpenRows({});
        }
    }, [open, currentEditedItem?.id, fetchRelatedPayments, fetchAccountCards]);

    const toggleRow = (id) => {
        setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // Renders a payment-method detail table (checks / credit cards / cash / bank transactions).
    const renderLineTable = (title, lines, itemType) => {
        if (!lines || lines.length === 0) return null;
        const isCheck = itemType === 'checks';
        const isBank = itemType === 'bankTransactions';
        return (
            <Box sx={{ margin: '4px 0', padding: 1, backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#304FFF', marginBottom: '4px' }}>
                    {title}
                </Typography>
                <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '2px 6px', fontSize: '11px' } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={subHeaderCellSx}>{t('date') || 'Date'}</TableCell>
                            {isCheck && <TableCell sx={subHeaderCellSx}>{t('CheckNumber') || 'Check #'}</TableCell>}
                            <TableCell sx={subHeaderCellSx}>{t('amount') || 'Amount'}</TableCell>
                            {!isCheck && <TableCell sx={subHeaderCellSx}>{t('Description') || 'Description'}</TableCell>}
                            {(isCheck || isBank) && <TableCell sx={subHeaderCellSx}>{t('bankName') || 'Bank'}</TableCell>}
                            {(isCheck || isBank) && <TableCell sx={subHeaderCellSx}>{t('supplierAccountNumber') || 'Account #'}</TableCell>}
                            <TableCell sx={subHeaderCellSx}>{t('Reference2') || 'Reference 2'}</TableCell>
                            <TableCell sx={subHeaderCellSx}>{t('Debit Account') || 'Debit'}</TableCell>
                            <TableCell sx={subHeaderCellSx}>{t('Credit Account') || 'Credit'}</TableCell>
                            <TableCell sx={subHeaderCellSx}>{t('Document') || 'Document'}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {lines.map((line, idx) => (
                            <TableRow key={line.id || idx}>
                                <TableCell>{formatDate(line.paymentDate)}</TableCell>
                                {isCheck && <TableCell>{line.description || '-'}</TableCell>}
                                <TableCell>{beautifyNumber(line.amount)}</TableCell>
                                {!isCheck && <TableCell>{line.description || '-'}</TableCell>}
                                {(isCheck || isBank) && <TableCell>{line.bankName || '-'}</TableCell>}
                                {(isCheck || isBank) && <TableCell>{line.supplierAccountNumber || '-'}</TableCell>}
                                <TableCell>{line.reference2 || '-'}</TableCell>
                                <TableCell>{accountName(line.debitAccount)}</TableCell>
                                <TableCell>{accountName(line.creditAccount)}</TableCell>
                                <TableCell>
                                    {line.documentUrl ? (
                                        <span
                                            onClick={() => openPdf(line.documentUrl)}
                                            style={{ color: '#304FFF', cursor: 'pointer', textDecoration: 'underline' }}
                                        >
                                            {t('Document') || 'Document'}
                                        </span>
                                    ) : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        );
    };

    const colSpan = 8;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    direction: direction,
                    maxWidth: '75vw',
                }
            }}
        >
            <DialogTitle dir={direction} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{t('RelatedPayments') || 'Related Payments'}</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                        <Typography>{t('Loading') || 'Loading...'}</Typography>
                    </Box>
                ) : relatedPayments.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                        <Typography>{t('NoRelatedPaymentsFound') || 'No related payments found'}</Typography>
                    </Box>
                ) : (
                    <TableContainer dir={direction} component={Paper} sx={{ maxHeight: 460 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headerCellSx} />
                                    <TableCell sx={headerCellSx}>{t('UploadNumber') || 'Upload #'}</TableCell>
                                    <TableCell sx={headerCellSx}>{t('Type') || 'Type'}</TableCell>
                                    <TableCell sx={headerCellSx}>{t('AccountCard') || 'Account'}</TableCell>
                                    <TableCell sx={headerCellSx}>{t('Description') || 'Description'}</TableCell>
                                    <TableCell sx={headerCellSx}>{t('Reference') || 'Reference'}</TableCell>
                                    <TableCell sx={headerCellSx}>{t('Amount') || 'Amount'}</TableCell>
                                    <TableCell sx={headerCellSx}>{t('Document Date') || 'Date'}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {relatedPayments.map((payment) => {
                                    const isOpen = !!openRows[payment.id];
                                    const hasLines =
                                        (payment.checks?.length || 0) +
                                        (payment.creditCards?.length || 0) +
                                        (payment.cash?.length || 0) +
                                        (payment.bankTransactions?.length || 0) +
                                        (payment.offsets?.length || 0) > 0;
                                    return (
                                        <React.Fragment key={payment.id}>
                                            <TableRow
                                                hover
                                                onClick={() => handlePaymentRowClick(payment)}
                                                sx={{ '& td': { fontSize: 12 }, cursor: 'pointer' }}
                                            >
                                                <TableCell>
                                                    {hasLines && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRow(payment.id);
                                                            }}
                                                        >
                                                            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                                <TableCell>{payment.uploadNumber || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={payment.paymentAcceptanceType || '-'}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#FF9800',
                                                            color: '#ffffff',
                                                            fontSize: '10px',
                                                            height: '20px',
                                                            fontWeight: 600
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>{payment.accountCodeName || '-'}</TableCell>
                                                <TableCell>{payment.description || '-'}</TableCell>
                                                <TableCell>{payment.reference || '-'}</TableCell>
                                                <TableCell>{beautifyNumber(payment.amount)}</TableCell>
                                                <TableCell>{formatDate(payment.date)}</TableCell>
                                            </TableRow>
                                            {hasLines && (
                                                <TableRow>
                                                    <TableCell style={{ paddingTop: 0, paddingBottom: 0 }} colSpan={colSpan}>
                                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                            <Box sx={{ padding: '6px 4px' }}>
                                                                {renderLineTable(t('Checks') || 'Checks', payment.checks, 'checks')}
                                                                {renderLineTable(t('creditCards') || 'Credit Cards', payment.creditCards, 'creditCards')}
                                                                {renderLineTable(t('cash') || 'Cash', payment.cash, 'cash')}
                                                                {renderLineTable(t('bankTransactions') || 'Bank Transactions', payment.bankTransactions, 'bankTransactions')}
                                                                {renderLineTable(t('offsets') || 'Offsets', payment.offsets, 'offsets')}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default RelatedPaymentAcceptancesModal;
